import { apiErrorField, reachedServer, setTowerAuthToken } from '@agos/api-client'
import { tt } from '@agos/shared'

/**
 * The Control Tower's own session.
 *
 * **Nothing here mints a token.** `POST /central/auth/login` takes an e-mail and a password and the
 * platform signs an RS256 token with a private key no tenant holds — `TowerJwtSigner`: *"a franchise's
 * api-server holds only the public key, so it can verify that the platform vouched for somebody but
 * cannot mint a token that says so. Nothing running inside a tenant can promote itself."* That asymmetry
 * is the security model of the whole platform, and this file's job is to carry the result of a login,
 * not to reproduce it.
 *
 * The token is written to `hk_super_admin_token` — legacy's own key, byte-identical, so a super-admin
 * mid-session when the new tower deploys keeps it. `customFetch` reads it from there, after the staff and
 * customer keys, so on a machine that somehow holds several the most privileged one answers.
 */

export interface TowerUser {
  id: number
  email: string
  name: string
}

/**
 * A login answer that is actually a session.
 *
 * Both halves or neither, the same rule the customer auth pages apply: a 2xx carrying a user and no
 * token leaves the app looking signed in and unable to make one authorised call.
 */
export function towerSessionFrom(payload: unknown): { user: TowerUser; token: string } | null {
  if (typeof payload !== 'object' || payload === null) return null
  const row = payload as Record<string, unknown>
  const token = row.token
  if (typeof token !== 'string' || token.trim() === '') return null

  const user = typeof row.user === 'object' && row.user !== null ? (row.user as Record<string, unknown>) : row
  const email = typeof user.email === 'string' ? user.email : ''
  if (email === '') return null
  return {
    user: {
      id: typeof user.id === 'number' ? user.id : 0,
      email,
      name: typeof user.name === 'string' && user.name !== '' ? user.name : email,
    },
    token,
  }
}

export function signInTower(token: string): void {
  setTowerAuthToken(token)
}

export function signOutTower(): void {
  setTowerAuthToken(null)
}

export function maySubmitLogin(email: string, password: string): boolean {
  return email.trim() !== '' && password !== ''
}

/**
 * Why a login failed, in three cases the operator can act on differently.
 *
 * The server's own wording where there is any - the refusals here name the reason and are written for a
 * person - then a malformed answer, then a connection that never arrived.
 *
 * **§9.360: two of those three cases were unreachable.** It read
 * `(failure as { body?: { error?: unknown } }).body`, and `ApiError` has no `body` - the envelope is
 * in `data` (`custom-fetch.ts:189`). So the first branch never fired. Nor could the second: it
 * returned *"could not connect"* only when the value had no `response`, and **`ApiError` carries
 * one**, so every real refusal - wrong password included - fell through to the generic *"Prihlásenie
 * zlyhalo"* on the first screen an operator meets.
 *
 * **And its test passed**, because the test built `{ response: {}, body: { error: … } }` - a shape
 * `customFetch` has never thrown. An assertion that cannot fail says nothing; the tests below now
 * construct a real `ApiError` from a real `Response`.
 *
 * **The customer app already had this function, written correctly** - `customer-account`'s
 * `failureNote`, three lines over `apiErrorField` and `reachedServer`, both from `@agos/api-client`.
 * So the Tower's was not a first attempt at a hard problem; it was a second, separate attempt at a
 * solved one, and the wrong half of it is what shipped. This is now the same three lines rather than
 * a third spelling of the predicate: re-deriving *"did an answer arrive"* inline is how the
 * `'response' in failure` mistake got written in the first place.
 *
 * `apiErrorField` rather than `apiErrorMessage` because this has to tell *"the server said nothing
 * usable"* from *"the server was not reached"*, and it is the reader documented for the ambiguity -
 * it tries `data` and then `body`, for a hand-thrown object or a shape from before this client.
 */
export function loginFailureNote(failure: unknown): string {
  const said = apiErrorField(failure, 'error')
  if (said !== null) return said
  // `ApiError` carries a response; anything without one never reached the server.
  if (!reachedServer(failure)) return tt('Nepodarilo sa spojiť so serverom')
  // An answer arrived and refused without a sentence, or was not JSON at all.
  return tt('Prihlásenie zlyhalo')
}
